import { useEffect, useMemo, useState } from 'react'
import { Edit2, Loader2, Plus, ReceiptText, RefreshCw, RotateCcw, Trash2, Users, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { classesApi, feesApi } from '../api/services.js'
import PageHeader from '../components/PageHeader.jsx'
import { isAcademicYear } from '../utils/academicYear.js'

const FREQUENCIES = ['monthly', 'quarterly', 'half-yearly', 'annually', 'one-time']
const emptyComponent = { name: '', amount: '', frequency: 'monthly', description: '' }

// Generate 4 academic year options centred on the current year (Indian AY: Apr–Mar)
const getAcademicYearOptions = () => {
  const now = new Date()
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1
  return [y - 1, y, y + 1, y + 2].map((n) => `${n}-${n + 1}`)
}
const ACADEMIC_YEAR_OPTIONS = getAcademicYearOptions()
const defaultAcademicYear = () => ACADEMIC_YEAR_OPTIONS[1] // current AY

const emptyForm = {
  name: '',
  academicYear: defaultAcademicYear(),
  dueDay: '5',
  description: '',
  applyToAllClasses: false,
  classIds: [],
  components: [{ ...emptyComponent }],
}

const freqLabel = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  'half-yearly': 'Half-Yearly',
  annually: 'Annual',
  'one-time': 'One-Time',
}

const formatCurrency = (value = 0) => `₹${Number(value || 0).toLocaleString('en-IN')}`
const getClassId = (item) => item?._id || item?.id || item
const getClassLabel = (item) => `${item?.name || 'Class'}${item?.section ? ` — ${item.section}` : ''}`

export default function FeeStructuresPage() {
  const [structures, setStructures] = useState([])
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [deleteId, setDeleteId] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [generateTarget, setGenerateTarget] = useState(null)
  const [genForm, setGenForm] = useState({ academicYear: '', classId: '' })
  const [generating, setGenerating] = useState(false)
  const [reverting, setReverting] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const [structuresRes, classesRes] = await Promise.all([
        feesApi.getStructures(),
        classesApi.getAll(),
      ])
      setStructures(Array.isArray(structuresRes) ? structuresRes : [])
      setClasses(Array.isArray(classesRes) ? classesRes : [])
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load fee structures')
      setStructures([])
      setClasses([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const classLookup = useMemo(
    () =>
      Object.fromEntries(
        classes.map((item) => [getClassId(item), getClassLabel(item)])
      ),
    [classes]
  )

  const takenClassIds = useMemo(() => {
    const taken = new Set()
    structures.forEach((structure) => {
      const structureId = structure._id || structure.id
      if (editing && structureId === editing) return
      ;(structure.classIds || []).forEach((classItem) => {
        const classId = getClassId(classItem)
        if (classId) taken.add(classId)
      })
    })
    return taken
  }, [editing, structures])

  const openAdd = () => {
    setEditing(null)
    setForm(emptyForm)
    setModalOpen(true)
  }

  const openEdit = (structure) => {
    setEditing(structure._id || structure.id)
    setForm({
      name: structure.name || '',
      academicYear: structure.academicYear || '',
      dueDay: String(structure.dueDay || '5'),
      description: structure.description || '',
      applyToAllClasses: !(structure.classIds || []).length,
      classIds: (structure.classIds || []).map((item) => getClassId(item)).filter(Boolean),
      components: structure.components?.length
        ? structure.components.map((item) => ({
            name: item.name,
            amount: String(item.amount),
            frequency: item.frequency || 'monthly',
            description: item.description || '',
          }))
        : [{ ...emptyComponent }],
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditing(null)
    setForm(emptyForm)
  }

  const setComp = (index, field, value) => {
    setForm((prev) => {
      const nextComponents = [...prev.components]
      nextComponents[index] = { ...nextComponents[index], [field]: value }
      return { ...prev, components: nextComponents }
    })
  }

  const addComponent = () =>
    setForm((prev) => ({
      ...prev,
      components: [...prev.components, { ...emptyComponent }],
    }))

  const removeComponent = (index) =>
    setForm((prev) => ({
      ...prev,
      components: prev.components.filter((_, itemIndex) => itemIndex !== index),
    }))

  const toggleClassId = (classId) => {
    setForm((prev) => ({
      ...prev,
      applyToAllClasses: false,
      classIds: prev.classIds.includes(classId)
        ? prev.classIds.filter((item) => item !== classId)
        : [...prev.classIds, classId],
    }))
  }

  const toggleAllClasses = () => {
    setForm((prev) => ({
      ...prev,
      applyToAllClasses: !prev.applyToAllClasses,
      classIds: !prev.applyToAllClasses ? [] : prev.classIds,
    }))
  }

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Structure name is required')
      return
    }
    if (!form.academicYear.trim()) {
      toast.error('Academic year is required')
      return
    }
    if (!isAcademicYear(form.academicYear)) {
      toast.error('Use academic year format YYYY-YYYY')
      return
    }
    if (!form.applyToAllClasses && form.classIds.length === 0) {
      toast.error('Choose at least one class section or select All Classes')
      return
    }
    if (form.components.length === 0) {
      toast.error('Add at least one fee component')
      return
    }
    for (const component of form.components) {
      if (!component.name.trim()) {
        toast.error('Every fee component needs a name')
        return
      }
      if (!component.amount || Number(component.amount) <= 0) {
        toast.error('Every fee component needs a valid amount')
        return
      }
    }

    try {
      setSaving(true)
      const payload = {
        name: form.name.trim(),
        academicYear: form.academicYear.trim(),
        dueDay: Number(form.dueDay) || 5,
        description: form.description.trim(),
        classIds: form.applyToAllClasses ? [] : form.classIds,
        components: form.components.map((component) => ({
          name: component.name.trim(),
          amount: Number(component.amount),
          frequency: component.frequency,
          description: component.description?.trim() || '',
        })),
      }

      if (editing) {
        await feesApi.updateStructure(editing, payload)
        toast.success('Fee structure updated')
      } else {
        await feesApi.createStructure(payload)
        toast.success('Fee structure created')
      }

      closeModal()
      fetchData()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save fee structure')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      setDeleting(true)
      await feesApi.deleteStructure(deleteId)
      toast.success('Fee structure deleted')
      setDeleteId(null)
      fetchData()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete fee structure')
    } finally {
      setDeleting(false)
    }
  }

  const handleRevert = async () => {
    const structureId = generateTarget?._id || generateTarget?.id
    const assignedClassIds = (generateTarget?.classIds || []).map((item) => getClassId(item)).filter(Boolean)
    const targetClassIds = assignedClassIds.length ? assignedClassIds : classes.map((item) => getClassId(item)).filter(Boolean)
    if (!structureId || !genForm.academicYear.trim()) {
      toast.error('Structure and academic year are required')
      return
    }
    if (!isAcademicYear(genForm.academicYear)) {
      toast.error('Use academic year format YYYY-YYYY')
      return
    }
    const classIds = genForm.classId ? [genForm.classId] : targetClassIds

    if (!window.confirm(`This will delete all fee accounts for the selected class(es) under "${generateTarget?.name}" that have no payments recorded.\n\nAccounts with payments will be kept.\n\nContinue?`)) return

    try {
      setReverting(true)
      let totalDeleted = 0, totalSkipped = 0
      for (const classId of classIds) {
        const res = await feesApi.revertAccounts({ structureId, academicYear: genForm.academicYear.trim(), classId })
        totalDeleted += res?.deleted ?? 0
        totalSkipped += res?.skipped ?? 0
      }
      const note = totalSkipped > 0 ? ` (${totalSkipped} kept — payments recorded)` : ''
      toast.success(`Reverted ${totalDeleted} account(s)${note}`)
      setGenerateTarget(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to revert fee accounts')
    } finally {
      setReverting(false)
    }
  }

  const openGenerate = (structure) => {
    const structureClassIds = (structure.classIds || []).map((item) => getClassId(item)).filter(Boolean)

    setGenerateTarget(structure)
    setGenForm({
      academicYear: structure.academicYear || '',
      classId: '',
    })
  }

  const handleGenerate = async () => {
    const structureId = generateTarget?._id || generateTarget?.id
    const assignedClassIds = (generateTarget?.classIds || []).map((item) => getClassId(item)).filter(Boolean)
    const targetClassIds = assignedClassIds.length ? assignedClassIds : classes.map((item) => getClassId(item)).filter(Boolean)

    if (!structureId || !genForm.academicYear.trim()) {
      toast.error('Structure and academic year are required')
      return
    }
    if (!isAcademicYear(genForm.academicYear)) {
      toast.error('Use academic year format YYYY-YYYY')
      return
    }
    if (!genForm.classId && targetClassIds.length === 0) {
      toast.error('No class sections available for account generation')
      return
    }

    try {
      setGenerating(true)
      await feesApi.generateAccounts({
        structureId,
        academicYear: genForm.academicYear.trim(),
        ...(genForm.classId
          ? { classId: genForm.classId }
          : { classIds: targetClassIds }),
      })
      toast.success('Fee accounts generated successfully')
      setGenerateTarget(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate fee accounts')
    } finally {
      setGenerating(false)
    }
  }

  const totalAmount = (structure) =>
    Number(structure.totalAmount || 0) || structure.components?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fee Structures"
        subtitle="Create fee templates, assign them to class sections or all classes, and generate accounts like the Edubridge RN admin flow."
        chips={[
          loading ? 'Loading structures' : `${structures.length} structures`,
          loading ? 'Loading sections' : `${classes.length} class sections`,
          form.applyToAllClasses ? 'All classes selected' : form.classIds?.length ? `${form.classIds.length} sections selected` : 'Class scope required',
        ]}
        actions={
          <>
            <button onClick={fetchData} className="btn-secondary">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={openAdd} className="btn-primary">
              <Plus className="w-4 h-4" /> New Structure
            </button>
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {loading ? (
          [...Array(6)].map((_, index) => (
            <div key={index} className="card p-5 animate-pulse space-y-4">
              <div className="h-10 w-10 rounded-2xl bg-gray-100" />
              <div className="h-5 w-1/2 rounded bg-gray-100" />
              <div className="h-4 w-2/3 rounded bg-gray-100" />
              <div className="space-y-2">
                <div className="h-3 rounded bg-gray-100" />
                <div className="h-3 rounded bg-gray-100" />
              </div>
            </div>
          ))
        ) : structures.length === 0 ? (
          <div className="empty-state card col-span-full">
            <ReceiptText className="w-10 h-10 text-gray-200" />
            <p className="empty-state-title">No fee structures yet</p>
            <p className="empty-state-copy">Create your first structure, assign it to class sections, and then generate student fee accounts from it.</p>
          </div>
        ) : (
          structures.map((structure) => {
            const structureId = structure._id || structure.id
            const assignedClasses = (structure.classIds || [])
              .map((item) => classLookup[getClassId(item)] || getClassLabel(item))
              .filter(Boolean)

            return (
              <div key={structureId} className="card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
                    <ReceiptText className="w-5 h-5" />
                  </div>
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                    {structure.academicYear || '—'}
                  </span>
                </div>

                <div className="mt-4">
                  <h3 className="text-lg font-semibold text-gray-900">{structure.name}</h3>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{formatCurrency(totalAmount(structure))}</p>
                  {structure.description ? <p className="text-sm text-gray-500 mt-2">{structure.description}</p> : null}
                </div>

                <div className="mt-5 space-y-2">
                  {structure.components?.map((item, index) => (
                    <div key={item._id || `${item.name}-${index}`} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-gray-600">{item.name}</span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(item.amount || 0)} <span className="text-gray-400">/ {freqLabel[item.frequency] || item.frequency}</span>
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 rounded-2xl bg-gray-50 px-4 py-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-gray-400 font-semibold">Assigned Sections</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {assignedClasses.length > 0 ? (
                      assignedClasses.map((label) => (
                        <span key={label} className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600">
                          {label}
                        </span>
                      ))
                    ) : (
                      <span className="rounded-full border border-primary-100 bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">All class sections</span>
                    )}
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <button onClick={() => openGenerate(structure)} className="btn-primary flex-1 min-w-[150px]">
                    <Users className="w-4 h-4" /> Generate Accounts
                  </button>
                  <button onClick={() => openEdit(structure)} className="btn-secondary flex-1 min-w-[120px]">
                    <Edit2 className="w-4 h-4" /> Edit
                  </button>
                  <button
                    onClick={() => setDeleteId(structureId)}
                    className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-2 font-semibold text-red-600 transition-colors hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-4xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{editing ? 'Edit Fee Structure' : 'Create Fee Structure'}</h3>
                <p className="text-sm text-gray-500 mt-1">Use All Classes or pick the class sections this fee plan applies to.</p>
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid gap-0 xl:grid-cols-[minmax(0,1fr)_320px]">
              <div className="max-h-[78vh] overflow-y-auto px-6 py-5 space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Structure Name *</label>
                    <input
                      className="input-field w-full"
                      value={form.name}
                      onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                      placeholder="e.g. Class 10A Annual Plan"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Academic Year *</label>
                    <div className="flex flex-wrap gap-2">
                      {ACADEMIC_YEAR_OPTIONS.map((yr) => (
                        <button
                          key={yr}
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, academicYear: yr }))}
                          className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                            form.academicYear === yr
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-primary-300'
                          }`}
                        >
                          {yr}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Due Day of Month</label>
                    <input
                      type="number"
                      min="1"
                      max="28"
                      className="input-field w-full"
                      value={form.dueDay}
                      onChange={(event) => setForm((prev) => ({ ...prev, dueDay: event.target.value }))}
                      placeholder="5"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                    <textarea
                      className="input-field w-full h-24 resize-none"
                      value={form.description}
                      onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                      placeholder="Optional notes for admins"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Fee Components</p>
                      <p className="text-xs text-gray-500 mt-1">Break the structure into the billable items you actually collect.</p>
                    </div>
                    <button onClick={addComponent} className="text-sm font-semibold text-primary-600 hover:text-primary-700">
                      Add Component
                    </button>
                  </div>

                  <div className="space-y-3">
                    {form.components.map((component, index) => (
                      <div key={index} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <p className="text-sm font-semibold text-gray-700">Component {index + 1}</p>
                          {form.components.length > 1 ? (
                            <button onClick={() => removeComponent(index)} className="text-sm font-semibold text-red-500 hover:text-red-600">
                              Remove
                            </button>
                          ) : null}
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <input
                            className="input-field md:col-span-2"
                            value={component.name}
                            onChange={(event) => setComp(index, 'name', event.target.value)}
                            placeholder="e.g. Tuition Fee"
                          />
                          <input
                            type="number"
                            className="input-field"
                            value={component.amount}
                            onChange={(event) => setComp(index, 'amount', event.target.value)}
                            placeholder={`Amount per ${freqLabel[component.frequency] || 'Period'} (₹)`}
                          />
                          <select
                            className="input-field"
                            value={component.frequency}
                            onChange={(event) => setComp(index, 'frequency', event.target.value)}
                          >
                            {FREQUENCIES.map((item) => (
                              <option key={item} value={item}>{freqLabel[item]}</option>
                            ))}
                          </select>
                          <textarea
                            className="input-field md:col-span-2 h-20 resize-none"
                            value={component.description}
                            onChange={(event) => setComp(index, 'description', event.target.value)}
                            placeholder="Optional component note"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 xl:border-l xl:border-t-0 px-6 py-5 max-h-[78vh] overflow-y-auto">
                <div className="rounded-2xl bg-blue-50 px-4 py-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-blue-500 font-semibold">Assigned Sections</p>
                  <p className="text-sm text-blue-700 mt-2">Choose All Classes or select specific sections for this structure.</p>
                </div>

                <div className="mt-5 space-y-2">
                  <button
                    onClick={toggleAllClasses}
                    className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
                      form.applyToAllClasses ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-primary-50/40'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className={`font-semibold ${form.applyToAllClasses ? 'text-primary-700' : 'text-gray-700'}`}>All Classes</span>
                      <span className={`text-xs font-semibold ${form.applyToAllClasses ? 'text-primary-600' : 'text-gray-400'}`}>
                        {form.applyToAllClasses ? 'Selected' : 'Available'}
                      </span>
                    </div>
                  </button>
                  {classes.map((classItem) => {
                    const classId = getClassId(classItem)
                    const isSelected = form.classIds.includes(classId)

                    return (
                      <button
                        key={classId}
                        onClick={() => toggleClassId(classId)}
                        disabled={form.applyToAllClasses}
                        className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
                          isSelected
                            ? 'border-primary-300 bg-primary-50'
                            : form.applyToAllClasses
                            ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                            : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-primary-50/40'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className={`font-medium ${isSelected ? 'text-primary-700' : 'text-gray-700'}`}>
                            {getClassLabel(classItem)}
                          </span>
                          <span className={`text-xs font-semibold ${isSelected ? 'text-primary-600' : 'text-gray-400'}`}>
                            {isSelected ? 'Selected' : form.applyToAllClasses ? 'Included' : 'Available'}
                          </span>
                        </div>
                      </button>
                    )
                  })}
                </div>

                <div className="mt-6 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-gray-400 font-semibold">Preview</p>
                  <div className="mt-4 space-y-3 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Sections</span>
                      <span className="font-semibold text-gray-900">{form.applyToAllClasses ? 'All' : form.classIds.length}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Components</span>
                      <span className="font-semibold text-gray-900">{form.components.length}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Total amount</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(form.components.reduce((sum, item) => sum + Number(item.amount || 0), 0))}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-3">
                  <button onClick={handleSave} disabled={saving} className="btn-primary w-full">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ReceiptText className="w-4 h-4" />}
                    {editing ? 'Save Structure' : 'Create Structure'}
                  </button>
                  <button onClick={closeModal} className="btn-secondary w-full">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900">Delete fee structure?</h3>
            <p className="text-sm text-gray-500 mt-2">This removes the structure from the admin list. Use this only if you are sure it is no longer needed.</p>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setDeleteId(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700 disabled:opacity-60">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {generateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Generate Fee Accounts</h3>
                <p className="text-sm text-gray-500 mt-1">{generateTarget.name}</p>
              </div>
              <button onClick={() => setGenerateTarget(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="rounded-2xl bg-gray-50 px-4 py-4 text-sm text-gray-600">
                Accounts will be generated only for the selected class section, or for every applicable section if you leave the filter blank.
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Academic Year *</label>
                <input
                  className="input-field w-full"
                  value={genForm.academicYear}
                  onChange={(event) => setGenForm((prev) => ({ ...prev, academicYear: event.target.value }))}
                  placeholder="2025-26"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Class Section Filter</label>
                <select
                  className="input-field w-full"
                  value={genForm.classId}
                  onChange={(event) => setGenForm((prev) => ({ ...prev, classId: event.target.value }))}
                >
                  <option value="">All applicable sections</option>
                  {((generateTarget.classIds || []).length ? generateTarget.classIds : classes).map((item) => {
                    const classId = getClassId(item)
                    return (
                      <option key={classId} value={classId}>
                        {classLookup[classId] || getClassLabel(item)}
                      </option>
                    )
                  })}
                </select>
              </div>
            </div>

            <div className="flex gap-3 justify-between border-t border-gray-100 px-6 py-5">
              <button
                onClick={handleRevert}
                disabled={reverting || generating}
                className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
              >
                {reverting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Revert Accounts
              </button>
              <div className="flex gap-3">
                <button onClick={() => setGenerateTarget(null)} className="btn-secondary">Cancel</button>
                <button onClick={handleGenerate} disabled={generating || reverting} className="btn-primary">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                  Generate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
